import { useEffect, useRef, useState } from "react";
import { FormInput } from "../../Inputs/FormInput";
import { useUserLocale } from "@/hooks/useUserLocale";
import {
  formatIntegerGroupedLive,
  stripIntegerGrouping,
  useLiveGroupedInput,
} from "@/lib/utils/number-input-format";
import {
  MAX_LONG_LENGTH,
  VALID_LONG_PATTERN,
} from "@/lib/utils/default-returner";

type TableCellContentLongProps = {
  onFocusAction?: () => void;
  onBlurAction?: () => void;
  isFilter?: boolean;
  border?: string;
  editable: boolean;
  /** Class for the read-only value; the table leaves it unset and inherits. */
  readTextClass?: string;
  value: string;
  isSelected: boolean;
  isEditing: boolean;
  onChangeValue: (value: string) => void;
  error?: string | null;
  height?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
  errorVisibility?: boolean;
  margin?: string;
  className?: string;
  label?: string;
  placeholder?: string;
  viewOnly?: boolean;
};

export const TableCellContentLong: React.FC<TableCellContentLongProps> = ({
  isFilter,
  border = "outline-tertiary border focus-within:outline-focus",
  editable,
  readTextClass,
  value,
  isSelected,
  isEditing,
  onChangeValue,
  onFocusAction,
  onBlurAction,
  error,
  height = "h-9",
  surface = "",
  fontSize = "body-2-regular",
  padding = "",
  rounded = "rounded-lg",
  errorVisibility = false,
  margin = "",
  className,
  label,
  placeholder,
  viewOnly,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const locale = useUserLocale();
  // Live thousand-separator grouping while typing ("10000" → "10.000").
  const formatLive = useLiveGroupedInput({ inputRef, locale, mode: "integer" });
  // `draft` holds the GROUPED display string; the canonical integer (no
  // grouping, kept as a string so long values never lose precision) is what we
  // validate and store.
  const editableOf = (v: string) =>
    isNaN(Number(v)) ? "" : formatIntegerGroupedLive(v, locale);
  const [draft, setDraft] = useState(() => editableOf(value));
  // What THIS cell last handed to the host, so the sync effect ignores the
  // host echoing it back: committing "" for a lone "-" must not wipe the "-"
  // the user is still typing after.
  const lastCommitted = useRef<string>(value ?? "");
  const lastLocale = useRef(locale);

  useEffect(() => {
    // A locale switch regroups the draft even when the value is our own echo.
    const localeChanged = lastLocale.current !== locale;
    lastLocale.current = locale;
    if (!localeChanged && (value ?? "") === lastCommitted.current) return;
    lastCommitted.current = value ?? "";
    setDraft(editableOf(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, locale]);

  const commit = (v: string) => {
    lastCommitted.current = v;
    onChangeValue(v);
  };

  function setLong(raw: string) {
    // stripIntegerGrouping("-") → "-", so a lone minus is preserved.
    const canonical = stripIntegerGrouping(raw, locale);
    const valid =
      VALID_LONG_PATTERN.test(canonical) && canonical.length <= MAX_LONG_LENGTH;
    if (valid || canonical === "" || canonical === "-") {
      setDraft(formatLive(raw));
      // The bare "-" reaches the host as it always has: the field-constraint
      // editor validates it and shows "invalid number" on purpose (pinned by
      // e2e additional-field-editor I2), and every other host treats it like
      // any other unparseable draft.
      commit(canonical);
    }
  }

  // Blur: an unstorable draft is cleared AND the host is told — the old branch
  // only cleared the box, so the host kept whatever it held before.
  const handleChange = () => {
    const canonical = stripIntegerGrouping(draft, locale);
    if (VALID_LONG_PATTERN.test(canonical) && canonical.length <= MAX_LONG_LENGTH) {
      commit(canonical);
      setDraft(formatIntegerGroupedLive(canonical, locale));
    } else {
      setDraft("");
      // Only a box that HELD something: an untouched empty box — a guest
      // without a value, a legacy non-numeric value the box never showed —
      // must not turn a tab-through into an edit (audit 2026-09-15).
      if (draft !== "") commit("");
    }
    if (onBlurAction) onBlurAction();
  };

  return (
    <>
      {isEditing && isSelected && (editable || isFilter) ? (
        <div className={`flex flex-col flex-1 ${className}`}>
          <FormInput
            ref={inputRef}
            id={`edit`}
            border={border}
            rounded={rounded}
            margin={margin}
            disabled={viewOnly}
            iconPaddingLeft=""
            iconPaddingRight=""
            label={label}
            height={height}
            fontSize={fontSize}
            placeholder={placeholder}
            surface={surface}
            paddingInput={padding}
            value={draft}
            onChangeAction={setLong}
            onEnterAction={() => inputRef.current?.blur()}
            onBlurAction={handleChange}
            onFocusAction={onFocusAction}
            width="w-full"
          />
          {error && (
            <p
              className={`mt-1 body-3-regular ${
                errorVisibility ? "font-error" : "opacity-0"
              }`}
            >
              {error}
            </p>
          )}
        </div>
      ) : (
        <p className={readTextClass}>
          {value != null && value !== "" ? editableOf(value) : ""}
        </p>
      )}
    </>
  );
};
